<template>
  <div class="template-shell">
    <div v-if="homePage" class="home-container">
      <component
        :is="dynamicComponent"
        :homeImage="homeImage"
        :exhibitIdProps="id"
        @exit="exitHomePage"
      />
    </div>

    <main v-else class="carousel-gallery">
      <header class="gallery-header">
        <h1>{{ exhibitName }}</h1>
      </header>

      <div v-if="loading" class="state-panel">Chargement...</div>
      <div v-else-if="resources.length === 0" class="state-panel">Aucune ressource dans la galerie</div>

      <section v-else class="carousel-stage">
        <button class="nav-button nav-button--left" type="button" @click="scrollCarousel(-1)" aria-label="Précédent">
          <q-icon name="chevron_left" />
        </button>

        <div ref="trackRef" class="carousel-track">
          <article
            v-for="(resource, index) in resources"
            :key="resource.id || index"
            class="carousel-card"
            :class="{ active: index === activeIndex }"
            @click="openLightbox(index)"
            @focusin="activeIndex = index"
          >
            <img
              v-if="isImageResource(resource) && resourceUrls[index]"
              :src="resourceUrls[index]"
              :alt="resource.title || exhibitName"
              loading="lazy"
            />
            <video v-else-if="getResourceType(resource) === 'video' && resourceUrls[index]" :src="resourceUrls[index]" muted playsinline />
            <div v-else class="file-card">
              <q-icon :name="fileIcon(resource)" />
              <span>{{ resourceUrls[index] === null ? 'Media indisponible' : resource.title || resource.name || 'Chargement...' }}</span>
            </div>
            <div class="card-caption">
              <span>{{ String(index + 1).padStart(2, '0') }}</span>
              <strong>{{ resource.title || 'Ressource' }}</strong>
            </div>
          </article>
        </div>

        <button class="nav-button nav-button--right" type="button" @click="scrollCarousel(1)" aria-label="Suivant">
          <q-icon name="chevron_right" />
        </button>
      </section>
    </main>

    <transition name="fade">
      <div v-if="activeLightboxIndex !== null" class="lightbox" @click="closeLightbox">
        <div class="lightbox-content" @click.stop>
          <button class="close-button" type="button" @click="closeLightbox" aria-label="Fermer">
            <q-icon name="close" />
          </button>
          <button class="lightbox-nav lightbox-nav--prev" type="button" @click="navigateLightbox(-1)" aria-label="Précédent">
            <q-icon name="chevron_left" />
          </button>
          <img v-if="isImageResource(activeResource) && activeUrl" :src="activeUrl" :alt="activeResource.title || exhibitName" />
          <video v-else-if="getResourceType(activeResource) === 'video' && activeUrl" :src="activeUrl" controls autoplay playsinline />
          <div v-else class="file-card file-card--large">
            <q-icon :name="fileIcon(activeResource)" />
            <span>{{ activeUrl === null ? 'Media indisponible' : activeResource.title || activeResource.name || 'Chargement...' }}</span>
          </div>
          <button class="lightbox-nav lightbox-nav--next" type="button" @click="navigateLightbox(1)" aria-label="Suivant">
            <q-icon name="chevron_right" />
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, defineProps, ref } from 'vue';
import { useGalleryTemplate } from '../../composables/useGalleryTemplate';

const props = defineProps({
  homePageProps: { type: Boolean, default: true },
  exhibitId: { type: String, default: null },
});

const {
  dynamicComponent,
  exhibitName,
  exitHomePage,
  getResourceType,
  homeImage,
  homePage,
  id,
  isImageResource,
  loading,
  resourceUrls,
  resources,
} = useGalleryTemplate(props);

const trackRef = ref(null);
const activeIndex = ref(0);
const activeLightboxIndex = ref(null);

const activeResource = computed(() => resources.value[activeLightboxIndex.value] || {});
const activeUrl = computed(() => resourceUrls.value[activeLightboxIndex.value] || '');

const scrollCarousel = (direction) => {
  if (!trackRef.value) return;
  const card = trackRef.value.querySelector('.carousel-card');
  const amount = card ? card.getBoundingClientRect().width + 20 : 420;
  trackRef.value.scrollBy({ left: direction * amount, behavior: 'smooth' });
  activeIndex.value = Math.max(0, Math.min(resources.value.length - 1, activeIndex.value + direction));
};

const openLightbox = (index) => {
  activeIndex.value = index;
  activeLightboxIndex.value = index;
};

const closeLightbox = () => {
  activeLightboxIndex.value = null;
};

const navigateLightbox = (direction) => {
  if (!resources.value.length || activeLightboxIndex.value === null) return;
  activeLightboxIndex.value = (activeLightboxIndex.value + direction + resources.value.length) % resources.value.length;
};

const fileIcon = (resource) => {
  const type = getResourceType(resource);
  if (type === 'audio') return 'audiotrack';
  if (type === 'pdf' || type === 'doc') return 'description';
  return 'insert_drive_file';
};
</script>

<style scoped>
.template-shell {
  min-height: 100vh;
  background: #000;
  color: #fff;
}

.carousel-gallery {
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 100vh;
  overflow: hidden;
  padding: 86px 0 42px;
}

.gallery-header {
  text-align: center;
  padding: 0 24px 48px;
  animation: titleIn 520ms ease both;
}

h1 {
  margin: 0;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 72px;
  font-weight: 400;
  line-height: 1;
}

.carousel-stage {
  position: relative;
  min-height: 0;
}

.carousel-track {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(320px, 28vw);
  gap: 20px;
  height: min(640px, 68vh);
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding: 0 max(64px, calc((100vw - 1420px) / 2)) 12px;
}

.carousel-track::-webkit-scrollbar {
  display: none;
}

.carousel-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: #161616;
  cursor: pointer;
  scroll-snap-align: center;
  animation: cardIn 520ms ease both;
  transition: transform 180ms ease, opacity 180ms ease;
}

.carousel-card:hover {
  transform: translateY(-4px);
}

.carousel-card:active {
  transform: scale(0.985);
}

.carousel-card img,
.carousel-card video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 560ms ease, filter 180ms ease;
}

.carousel-card:hover img,
.carousel-card:hover video {
  transform: scale(1.035);
  filter: saturate(1.08);
}

.card-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  padding: 48px 18px 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.76), transparent);
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 160ms ease, transform 160ms ease;
}

.carousel-card:hover .card-caption,
.carousel-card.active .card-caption {
  opacity: 1;
  transform: translateY(0);
}

.card-caption span {
  color: #d7d7d7;
  font-size: 13px;
}

.card-caption strong {
  max-width: 75%;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-button,
.close-button,
.lightbox-nav {
  border: 0;
  cursor: pointer;
}

.nav-button {
  position: absolute;
  top: 50%;
  z-index: 4;
  display: grid;
  place-items: center;
  width: 86px;
  height: 86px;
  border-radius: 50%;
  background: #fff;
  color: #000;
  font-size: 58px;
  transform: translateY(-50%);
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.nav-button:hover {
  box-shadow: 0 14px 40px rgba(255, 255, 255, 0.18);
}

.nav-button:active {
  transform: translateY(-50%) scale(0.94);
}

.nav-button--left {
  left: 48px;
}

.nav-button--right {
  right: 48px;
}

.file-card {
  display: grid;
  place-items: center;
  gap: 14px;
  height: 100%;
  padding: 28px;
  text-align: center;
  background: #171717;
}

.file-card .q-icon {
  font-size: 58px;
}

.state-panel {
  display: grid;
  place-items: center;
  min-height: 55vh;
  color: #d5d5d5;
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 22px;
  background: rgba(0, 0, 0, 0.9);
}

.lightbox-content {
  position: relative;
  display: grid;
  place-items: center;
  width: min(1180px, 94vw);
  height: min(820px, 88vh);
}

.lightbox-content img,
.lightbox-content video {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.close-button,
.lightbox-nav {
  position: absolute;
  z-index: 2;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #fff;
  color: #000;
}

.close-button {
  top: 10px;
  right: 10px;
  width: 44px;
  height: 44px;
}

.lightbox-nav {
  top: 50%;
  width: 58px;
  height: 58px;
  font-size: 34px;
  transform: translateY(-50%);
}

.lightbox-nav--prev {
  left: 10px;
}

.lightbox-nav--next {
  right: 10px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes titleIn {
  from {
    opacity: 0;
    transform: translateY(-16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 980px) {
  .carousel-gallery {
    padding-top: 52px;
  }

  h1 {
    font-size: 52px;
  }

  .carousel-track {
    grid-auto-columns: minmax(270px, 72vw);
    height: 62vh;
    padding-inline: 26px;
  }

  .nav-button {
    width: 62px;
    height: 62px;
    font-size: 42px;
  }

  .nav-button--left {
    left: 18px;
  }

  .nav-button--right {
    right: 18px;
  }
}

@media (max-width: 620px) {
  .gallery-header {
    padding-bottom: 30px;
  }

  h1 {
    font-size: 42px;
  }

  .nav-button {
    display: none;
  }
}
</style>
